import {useEffect, useRef, useState} from 'react'
import {useGameStore} from '../../../store/GameStoreContext.ts'
import {useIsReplay} from '../../../store/selectors/useIsReplay.ts'
import {getCostInformation} from '../../UIGameHelpButtons/UIGameHelpInformationButton/UIGameHelpCostsInformation/UIGameCostsInformation.ts'
import {ActionState, TurnState} from '@colonist/game-states'
import {CardValidatorOrdered} from '@colonist/game-mechanic-validators'
import {ModeSettingEnum} from '@colonist/types'
import {Sound} from '../../../utils/sound.ts'
import {useCurrentStateValidator} from '../../../store/selectors/useCurrentStateValidator.ts'
import {useMechanicValidatorDevelopmentCard} from '../../../store/selectors/useMechanicValidatorDevelopmentCard.ts'
import {useMechanicValidatorProgressCards} from '../../../store/selectors/useMechanicValidatorProgressCards.ts'

const SUGGESTION_TIMEOUT = 5000
const SUGGESTION_DURATION = 4000

export const useEndTurnSuggestion = () => {
    const [shouldShowSuggestion, setShouldShowSuggestion] = useState(false)
    const endTurnSuggestionTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)
    const clearSuggestionTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)
    const alreadySuggestedToEndTurnRef = useRef(false)

    const myColor = useGameStore((state) => state.myColor)
    const isMyColorSpectator = useGameStore((state) => state.isMyColorSpectator)
    const isReplay = useIsReplay()
    const gameState = useGameStore((state) => state.gameState)
    const currentState = gameState.currentState
    const isShowTradePanel = useGameStore((state) => state.gameTrade.isShowTradePanel)
    const modeSetting = useGameStore((state) => state.gameSettings.modeSetting)
    const playerState = gameState.playerStates[myColor]
    const devCardsValidator = useMechanicValidatorDevelopmentCard()
    const progressCardsValidator = useMechanicValidatorProgressCards()
    const tradeState = gameState.tradeState

    const currentStateValidator = useCurrentStateValidator()
    const isMyTurn = currentStateValidator.currentActivePlayers.includes(myColor)
    const isTurnState = currentState.turnState === TurnState.Turn || currentState.turnState === TurnState.SpecialBuildPhase
    const isCurrentlyTakingAnyAction = currentStateValidator.getPlayerActionState(myColor) !== ActionState.None
    const arePlayersTrading = Object.keys(tradeState.activeOffers).length > 0

    const doIHaveEnoughResourcesToBuildAnything = (): boolean => {
        if(!playerState) return false

        const costInformation = getCostInformation(modeSetting)
        const resourceCards = new CardValidatorOrdered(playerState.resourceCards)

        for(const cost of costInformation) {
            if(resourceCards.hasCardsInInventory(cost.cost)) {
                return true
            }
        }
        return false
    }

    const doIHaveCardToPlay = (): boolean => {
        if(!playerState) return false

        if(devCardsValidator?.canPlayerUseAtLeastOneDevelopmentCard(myColor)) {
            return true
        }

        if(progressCardsValidator?.canPlayerUseAtLeastOneProgressCard(myColor)) {
            return true
        }

        return false
    }

    const shouldSuggestToEndTurn = (): boolean => {
        if(modeSetting === ModeSettingEnum.BeginnerModeClassic4P) return false
        if(isMyColorSpectator) return false
        if(isReplay) return false
        if(alreadySuggestedToEndTurnRef.current) return false
        if(!isMyTurn) return false
        if(!isTurnState) return false
        if(isCurrentlyTakingAnyAction) return false
        if(arePlayersTrading) return false
        if(isShowTradePanel) return false
        if(doIHaveEnoughResourcesToBuildAnything()) return false
        if(doIHaveCardToPlay()) return false

        return true
    }

    const clearEndTurnSuggestionTimeout = () => {
        if(endTurnSuggestionTimeoutRef.current) {
            clearTimeout(endTurnSuggestionTimeoutRef.current)
            endTurnSuggestionTimeoutRef.current = undefined
        }
        if(clearSuggestionTimeoutRef.current) {
            clearTimeout(clearSuggestionTimeoutRef.current)
            clearSuggestionTimeoutRef.current = undefined
        }
        setShouldShowSuggestion(false)
    }

    const triggerEndTurnSuggestionTimeout = () => {
        clearEndTurnSuggestionTimeout()
        endTurnSuggestionTimeoutRef.current = setTimeout(() => {
            // Show the suggestion and play sound
            setShouldShowSuggestion(true)
            Sound.playSound(Sound.SoundType.BeginnerHintActivated)
            alreadySuggestedToEndTurnRef.current = true

            // Clear the suggestion after the duration
            clearSuggestionTimeoutRef.current = setTimeout(() => {
                setShouldShowSuggestion(false)
            }, SUGGESTION_DURATION)
        }, SUGGESTION_TIMEOUT)
    }

    useEffect(() => {
        if(!isMyTurn) {
            alreadySuggestedToEndTurnRef.current = false
        }

        if(shouldSuggestToEndTurn()) {
            triggerEndTurnSuggestionTimeout()
        } else {
            clearEndTurnSuggestionTimeout()
        }

        return () => {
            clearEndTurnSuggestionTimeout()
        }
    }, [
        gameState,
        isShowTradePanel,
        isMyTurn,
        isTurnState,
        isCurrentlyTakingAnyAction,
        arePlayersTrading,
        isMyColorSpectator,
        isReplay,
        modeSetting,
    ])

    return {
        shouldShowSuggestion: shouldShowSuggestion,
    }
}
